import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
    Plus,
    Search,
    Edit,
    UserCheck,
    Mail,
    Phone,
    BookOpen,
    DollarSign,
    Users,
    Eye,
    ChevronLeft,
    ChevronRight,
    User,
    Calendar,
    Shield,
    ToggleLeft,
    ToggleRight,
    Clock,
    MapPin,
    UserX,
    RefreshCw
} from 'lucide-react';
import { collection, getDocs, doc, query, orderBy, addDoc, updateDoc, setDoc, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { db, auth, firebaseConfig } from '../firebase/config';
import { Teacher } from '../types';
import { useToast } from '../contexts/ToastContext';
import TeacherForm from '../components/forms/TeacherForm';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import ImagePreview from '../components/ui/ImagePreview';
import useTenantLimits from '../hooks/useTenantLimits';
import { recalculateTeachersSummary, onTeachersSummary, TeachersSummary } from '../services/summary.service';

const TeachersList: React.FC = () => {
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [selectedImageAlt, setSelectedImageAlt] = useState('');
    const [staffEmails, setStaffEmails] = useState<Set<string>>(new Set());
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showForm, setShowForm] = useState(false);
    const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
    const [viewingTeacher, setViewingTeacher] = useState<Teacher | null>(null);
    const [showInactiveReasonModal, setShowInactiveReasonModal] = useState(false);
    const [teacherToToggle, setTeacherToToggle] = useState<{ id: string, currentStatus: string } | null>(null);
    const [inactiveReason, setInactiveReason] = useState('');
    const [summary, setSummary] = useState<TeachersSummary>({ total: 0, active: 0, inactive: 0, resigned: 0 });
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; teacher: Teacher } | null>(null);
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const { isLimitReached, getLimit } = useTenantLimits();

    useEffect(() => {
        const unsub = onTeachersSummary(setSummary);
        return () => unsub();
    }, []);

    // Close context menu on outside click or scroll
    useEffect(() => {
        const close = () => setContextMenu(null);
        if (contextMenu) {
            window.addEventListener('click', close);
            window.addEventListener('scroll', close, true);
        }
        return () => {
            window.removeEventListener('click', close);
            window.removeEventListener('scroll', close, true);
        };
    }, [contextMenu]);

    useEffect(() => {
        const fetchStaffEmails = async () => {
            try {
                const staffRef = collection(db, 'staff');
                const querySnapshot = await getDocs(staffRef);
                const emails = new Set<string>();
                querySnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.email) {
                        emails.add(data.email.toLowerCase().trim());
                    }
                });
                setStaffEmails(emails);
            } catch (error) {
                console.error('Error fetching staff emails:', error);
            }
        };
        fetchStaffEmails();
    }, []);

    const handleImageClick = (imageUrl: string, teacherName: string) => {
        setSelectedImageUrl(imageUrl);
        setSelectedImageAlt(`${teacherName} - Profile Image`);
        setShowImageModal(true);
    };

    useEffect(() => {
        fetchTeachers();
    }, []);

    const fetchTeachers = async () => {
        try {
            const teachersRef = collection(db, 'teachers');
            const q = query(teachersRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const teachersData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dateOfBirth: doc.data().dateOfBirth?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
                currentPosition: {
                    ...doc.data().currentPosition,
                    joiningDate: doc.data().currentPosition?.joiningDate?.toDate(),
                },
            })) as Teacher[];
            setTeachers(teachersData);
        } catch (error) {
            console.error('Error fetching teachers:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleEdit = (teacher: Teacher) => {
        setEditingTeacher(teacher);
        setShowForm(true);
    };

    const handleFormClose = () => {
        setShowForm(false);
        setEditingTeacher(null);
    };

    const handleStatusToggle = (teacherId: string, currentStatus: string) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

        if (newStatus === 'inactive') {
            // Show modal for inactive reason
            setTeacherToToggle({ id: teacherId, currentStatus });
            setInactiveReason('');
            setShowInactiveReasonModal(true);
        } else {
            // Directly activate teacher
            updateTeacherStatus(teacherId, newStatus, '');
        }
    };

    const updateTeacherStatus = async (teacherId: string, newStatus: string, reason: string = '') => {
        try {
            const teacherRef = doc(db, 'teachers', teacherId);
            await updateDoc(teacherRef, {
                'currentPosition.status': newStatus,
                'currentPosition.inactiveReason': newStatus === 'inactive' ? reason : '',
                updatedAt: new Date()
            });

            // Update local state
            setTeachers(teachers.map(teacher =>
                teacher.id === teacherId
                    ? {
                        ...teacher,
                        currentPosition: {
                            ...teacher.currentPosition,
                            status: newStatus as 'active' | 'inactive' | 'resigned',
                            inactiveReason: newStatus === 'inactive' ? reason : ''
                        }
                    }
                    : teacher
            ));

            showSuccess('Status Updated', `Teacher status has been updated to ${newStatus}.`);
            recalculateTeachersSummary().catch(console.error);
        } catch (error) {
            console.error('Error updating teacher status:', error);
            showError('Update Failed', 'Failed to update teacher status. Please try again.');
        }
    };

    const handleInactiveReasonSubmit = () => {
        if (!teacherToToggle) return;

        const charCount = inactiveReason.trim().length;
        if (charCount < 5 || charCount > 15) {
            showError('Invalid Reason', 'Please provide a reason between 5-15 characters.');
            return;
        }

        updateTeacherStatus(teacherToToggle.id, 'inactive', inactiveReason);
        setShowInactiveReasonModal(false);
        setTeacherToToggle(null);
        setInactiveReason('');
    };

    const handleFormSubmit = async (teacherData: Omit<Teacher, 'id' | 'createdAt' | 'updatedAt'>) => {
        try {
            setLoading(true);

            if (editingTeacher) {
                // Update existing teacher
                const teacherRef = doc(db, 'teachers', editingTeacher.id);
                const { password, confirmPassword, ...updateData } = teacherData;
                await updateDoc(teacherRef, {
                    ...updateData,
                    updatedAt: new Date()
                });
                showSuccess('Teacher Updated', 'Teacher has been successfully updated.');
            } else {
                // First check if staff member with the same email already exists
                const staffRef = collection(db, 'staff');
                const staffQuery = query(staffRef, where('email', '==', teacherData.email.trim().toLowerCase()));
                const staffSnap = await getDocs(staffQuery);

                let uid = '';
                let linked = false;

                if (!staffSnap.empty) {
                    // Reuse existing UID
                    uid = staffSnap.docs[0].id;
                    linked = true;
                    console.log('Found existing staff with same email. Linking teacher. UID:', uid);
                } else {
                    // Create new teacher with Firebase Auth
                    if (!teacherData.password) {
                        showError('Password Required', 'Password is required for new teachers.');
                        return;
                    }

                    // Create a separate Firebase app instance to avoid interfering with current user session
                    const tempApp = initializeApp(firebaseConfig, 'temp-teacher-creation');
                    const tempAuth = getAuth(tempApp);

                    // Create Firebase Auth user using temporary auth instance
                    const userCredential = await createUserWithEmailAndPassword(
                        tempAuth,
                        teacherData.email,
                        teacherData.password
                    );
                    uid = userCredential.user.uid;

                    // Sign out from the temporary auth instance
                    await tempAuth.signOut();
                }

                // Create teacher document in Firestore using UID as document ID
                const { password, confirmPassword, ...teacherDoc } = teacherData;
                const teacherRef = doc(db, 'teachers', uid);
                console.log('Creating/setting teacher document with ID:', uid);
                await setDoc(teacherRef, {
                    ...teacherDoc,
                    uid: uid, // Link to Firebase Auth user
                    createdAt: new Date(),
                    updatedAt: new Date()
                });
                console.log('Teacher document created with ID:', teacherRef.id);

                if (linked) {
                    showSuccess('Teacher Linked', 'An existing staff member account with this email was found. This teacher profile has been linked to it successfully.');
                } else {
                    showSuccess('Teacher Created', 'Teacher account has been successfully created with authentication.');
                }
            }

            fetchTeachers(); // Refresh the list
            recalculateTeachersSummary().catch(console.error);
            handleFormClose();
        } catch (error: any) {
            console.error('Error saving teacher:', error);
            showError('Operation Failed', error.message || 'An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Filter and search teachers
    const filteredTeachers = useMemo(() => {
        return teachers.filter(teacher => {
            const matchesSearch =
                teacher.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                teacher.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                teacher.teacherId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                teacher.currentPosition.department.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesFilter = filterStatus === 'all' || teacher.currentPosition.status === filterStatus;

            return matchesSearch && matchesFilter;
        });
    }, [teachers, searchTerm, filterStatus]);

    // Pagination
    const totalPages = Math.ceil(filteredTeachers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedTeachers = filteredTeachers.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value: string) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1);
    };

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'inactive': return 'bg-yellow-100 text-yellow-800';
            case 'resigned': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const TeacherCard: React.FC<{ teacher: Teacher }> = ({ teacher }) => (
        <Card glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div 
                            className="w-12 h-12 rounded-full overflow-hidden shrink-0 cursor-pointer"
                            onClick={() => teacher.profileImage && handleImageClick(teacher.profileImage, `${teacher.firstName} ${teacher.lastName}`)}
                        >
                            {teacher.profileImage ? (
                                <img src={teacher.profileImage} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm">
                                    {`${teacher.firstName[0] ?? ''}${teacher.lastName?.[0] ?? ''}`.toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {teacher.firstName} {teacher.lastName}
                            </h3>
                            <p className="text-sm text-gray-600">{teacher.teacherId}</p>
                        </div>
                    </div>
                    <div className="flex space-x-1">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setViewingTeacher(teacher)}
                        >
                            <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEdit(teacher)}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                        <div className="flex flex-col items-center space-y-1">
                            <button
                                onClick={() => handleStatusToggle(teacher.id, teacher.currentPosition.status)}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                title={`Switch to ${teacher.currentPosition.status === 'active' ? 'inactive' : 'active'}`}
                            >
                                {teacher.currentPosition.status === 'active' ?
                                    <ToggleRight className="h-6 w-6 text-green-600" /> :
                                    <ToggleLeft className="h-6 w-6 text-gray-400" />
                                }
                            </button>
                            {teacher.currentPosition.status === 'inactive' && teacher.currentPosition.inactiveReason && (
                                <div className="text-xs text-red-600 text-center max-w-20 break-words">
                                    {teacher.currentPosition.inactiveReason}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span>{teacher.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4" />
                        <span>{teacher.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <BookOpen className="h-4 w-4" />
                        <span>{teacher.currentPosition.department}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <DollarSign className="h-4 w-4" />
                        <span>Rs. {teacher.currentPosition.salary.toLocaleString()}</span>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex space-x-2 flex-wrap gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(teacher.currentPosition.status)}`}>
                            {teacher.currentPosition.status}
                        </span>
                        {staffEmails.has(teacher.email.toLowerCase().trim()) && (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                                Dual Role: Teacher & Staff
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <BookOpen className="h-3 w-3" />
                        <span>{teacher.subjects?.length || 0} subjects</span>
                    </div>
                </div>

                {/* Subjects */}
                {teacher.subjects && teacher.subjects.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-2">Subjects:</p>
                        <div className="flex flex-wrap gap-1">
                            {teacher.subjects.slice(0, 3).map((subject, index) => (
                                <span
                                    key={index}
                                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                >
                                    {subject}
                                </span>
                            ))}
                            {teacher.subjects.length > 3 && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                    +{teacher.subjects.length - 3} more
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );

    const handleContextMenu = (e: React.MouseEvent, teacher: Teacher) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ x: e.clientX, y: e.clientY, teacher });
    };

    const TeacherTableRow: React.FC<{ teacher: Teacher }> = ({ teacher }) => (
        <tr
            className="hover:bg-gray-50 transition-colors duration-200 cursor-context-menu"
            onContextMenu={(e) => handleContextMenu(e, teacher)}
        >
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-3">
                    <div 
                        className="w-8 h-8 rounded-full overflow-hidden shrink-0 cursor-pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (teacher.profileImage) {
                                handleImageClick(teacher.profileImage, `${teacher.firstName} ${teacher.lastName}`);
                            }
                        }}
                    >
                        {teacher.profileImage ? (
                            <img src={teacher.profileImage} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-[10px]">
                                {`${teacher.firstName[0] ?? ''}${teacher.lastName?.[0] ?? ''}`.toUpperCase()}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="text-sm font-medium text-gray-900">
                            {teacher.firstName} {teacher.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{teacher.teacherId}</div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{teacher.email}</div>
                <div className="text-sm text-gray-500">{teacher.phone}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{teacher.currentPosition.department}</div>
                <div className="text-sm text-gray-500">{teacher.currentPosition.designation}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex flex-col gap-1 items-start">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(teacher.currentPosition.status)}`}>
                        {teacher.currentPosition.status}
                    </span>
                    {staffEmails.has(teacher.email.toLowerCase().trim()) && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-800">
                            Dual Role
                        </span>
                    )}
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                Rs. {teacher.currentPosition.salary.toLocaleString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <BookOpen className="h-3 w-3" />
                    <span>{teacher.subjects?.length || 0}</span>
                </div>
            </td>
        </tr>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Teachers</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Manage your school teachers and their information.</p>
                </div>
                <button
                    onClick={() => {
                        if (isLimitReached('Teachers', teachers.length)) {
                            const lim = getLimit('Teachers');
                            showError('Teacher Limit Reached', `You have reached the maximum limit of ${lim} teachers. Please contact Dynamix Zone to increase your Teachers limit.`);
                            return;
                        }
                        setShowForm(true);
                    }}
                    className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
                >
                    <Plus className="h-4 w-4" />
                    Create Teacher
                </button>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-blue-50 text-blue-500 shrink-0"><Users className="h-5 w-5" /></div>
                    <div><p className="text-xs text-gray-500 font-medium">Total Teachers</p><p className="text-2xl font-bold text-gray-900 mt-0.5">{summary.total}</p></div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600 shrink-0"><UserCheck className="h-5 w-5" /></div>
                    <div><p className="text-xs text-gray-500 font-medium">Active Teachers</p><p className="text-2xl font-bold text-gray-900 mt-0.5">{summary.active}</p></div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-red-50 text-red-500 shrink-0"><UserX className="h-5 w-5" /></div>
                    <div><p className="text-xs text-gray-500 font-medium">Inactive Teachers</p><p className="text-2xl font-bold text-gray-900 mt-0.5">{summary.inactive}</p></div>
                </div>
            </div>

            {/* Table card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2 className="text-base font-bold text-gray-900">Teacher Information</h2>
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search" className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 w-52" />
                        </div>
                        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white">
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="resigned">Resigned</option>
                        </select>
                        <button className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"><RefreshCw className="h-4 w-4" /></button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-100 bg-gray-50/60">
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Teacher</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Position</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Salary</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Subjects</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {paginatedTeachers.map((teacher) => (
                                <TeacherTableRow key={teacher.id} teacher={teacher} />
                                ))}
                            </tbody>
                        </table>
                    </div>

                {/* Pagination footer */}
                <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-gray-50/40">
                    <span className="text-xs text-gray-500">
                        {filteredTeachers.length === 0 ? '0' : `${startIndex + 1}-${Math.min(startIndex + itemsPerPage, filteredTeachers.length)}`} of {filteredTeachers.length} items
                    </span>
                    <div className="flex items-center gap-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button className="w-7 h-7 rounded-lg bg-red-500 text-white text-xs font-bold flex items-center justify-center">{currentPage}</button>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                        <select value={itemsPerPage} onChange={e => handleItemsPerPageChange(e.target.value)}
                            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white">
                            <option value={10}>10 / page</option>
                            <option value={25}>25 / page</option>
                            <option value={50}>50 / page</option>
                        </select>
                    </div>
                </div>
            </div>


            {/* Teacher Form Modal */}
            {showForm && (
                <TeacherForm
                    teacher={editingTeacher || undefined}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormClose}
                    loading={loading}
                />
            )}

            {/* Read-only View Modal */}
            {viewingTeacher && (
                <Modal
                    isOpen={true}
                    onClose={() => setViewingTeacher(null)}
                    title={`View Teacher - ${viewingTeacher.firstName} ${viewingTeacher.lastName}`}
                    size="full"
                >
                    <div className="space-y-8">
                        {/* Basic Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <User className="h-6 w-6 mr-3 text-emerald-600" />
                                Basic Information
                            </h3>
                            <div className="flex justify-center mb-6">
                                <div 
                                    className="w-20 h-20 rounded-full overflow-hidden cursor-pointer ring-4 ring-emerald-50 shadow-md"
                                    onClick={() => viewingTeacher.profileImage && handleImageClick(viewingTeacher.profileImage, `${viewingTeacher.firstName} ${viewingTeacher.lastName}`)}
                                >
                                    {viewingTeacher.profileImage ? (
                                        <img src={viewingTeacher.profileImage} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center text-white text-2xl font-bold">
                                            {`${viewingTeacher.firstName[0]}${viewingTeacher.lastName?.[0] ?? ''}`.toUpperCase()}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Teacher ID</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.teacherId}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.firstName}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.lastName}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.email}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.phone}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.dateOfBirth.toLocaleDateString()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900 capitalize">
                                        {viewingTeacher.gender}
                                    </div>
                                </div>
                                {staffEmails.has(viewingTeacher.email.toLowerCase().trim()) && (
                                    <div>
                                        <label className="block text-sm font-medium text-indigo-700 mb-2">Dual Role Status</label>
                                        <div className="px-3 py-2.5 border border-indigo-300 rounded-xl bg-indigo-50 text-indigo-900 font-semibold">
                                            Also Registered as Staff Member
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* Professional Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6">Professional Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Education</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.qualification.degree}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Experience (Years)</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.experience.totalYears}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.currentPosition.department}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.currentPosition.designation}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Joining Date</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingTeacher.currentPosition.joiningDate.toLocaleDateString()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Salary</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        Rs. {viewingTeacher.currentPosition.salary.toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900 capitalize">
                                        {viewingTeacher.currentPosition.status}
                                    </div>
                                </div>
                                {viewingTeacher.currentPosition.status === 'inactive' && viewingTeacher.currentPosition.inactiveReason && (
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Inactive Reason</label>
                                        <div className="px-3 py-2.5 border border-red-300 rounded-xl bg-red-50 text-red-900">
                                            {viewingTeacher.currentPosition.inactiveReason}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Subjects */}
                            {viewingTeacher.subjects && viewingTeacher.subjects.length > 0 && (
                                <div className="mt-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-3">Subjects</label>
                                    <div className="flex flex-wrap gap-2">
                                        {viewingTeacher.subjects.map((subject, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 text-emerald-800"
                                            >
                                                {subject}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* Teacher Schedule */}
                        {viewingTeacher.periods && viewingTeacher.periods.length > 0 && (
                            <Card className="p-6">
                                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                    <Clock className="h-6 w-6 mr-3 text-emerald-600" />
                                    Teaching Schedule
                                </h3>
                                <div className="overflow-x-auto">
                                    <div className="space-y-4">
                                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day, dayIndex) => {
                                            const dayOfWeek = dayIndex + 1;
                                            const dayPeriods = viewingTeacher.periods.filter(period => period.dayOfWeek === dayOfWeek);

                                            if (dayPeriods.length === 0) return null;

                                            return (
                                                <div key={day} className="border border-gray-200 rounded-lg">
                                                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                                                        <h4 className="font-medium text-gray-900">{day}</h4>
                                                    </div>
                                                    <div className="p-4">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                            {dayPeriods.map((period, index) => (
                                                                <div key={index} className="border border-gray-200 rounded-lg p-3">
                                                                    <div className="text-sm font-medium text-gray-900 mb-2">
                                                                        {period.startTime} - {period.endTime}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mb-2">
                                                                        Period {index + 1}
                                                                    </div>
                                                                    <div className={`p-2 rounded text-xs ${period.status === 'pending'
                                                                        ? 'bg-yellow-50 border border-yellow-200'
                                                                        : 'bg-emerald-50 border border-emerald-200'
                                                                        }`}>
                                                                        <div className={`font-medium ${period.status === 'pending'
                                                                            ? 'text-yellow-900'
                                                                            : 'text-emerald-900'
                                                                            }`}>
                                                                            {period.subject}
                                                                        </div>
                                                                        <div className={`${period.status === 'pending'
                                                                            ? 'text-yellow-700'
                                                                            : 'text-emerald-700'
                                                                            }`}>
                                                                            {period.className}
                                                                        </div>
                                                                        <div className={`text-xs mt-1 ${period.status === 'pending'
                                                                            ? 'text-yellow-600'
                                                                            : 'text-emerald-600'
                                                                            }`}>
                                                                            Status: {period.status}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </Card>
                        )}

                        {/* Modal Actions */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                            <Button
                                variant="secondary"
                                onClick={() => setViewingTeacher(null)}
                            >
                                Close
                            </Button>
                            <Button
                                onClick={() => {
                                    setViewingTeacher(null);
                                    handleEdit(viewingTeacher);
                                }}
                            >
                                Edit Teacher
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Inactive Reason Modal */}
            {showInactiveReasonModal && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowInactiveReasonModal(false)}
                    title="Provide Inactive Reason"
                    size="md"
                >
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reason for making teacher inactive *
                            </label>
                            <textarea
                                value={inactiveReason}
                                onChange={(e) => setInactiveReason(e.target.value)}
                                placeholder="Please provide a reason between 5-15 characters..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                rows={3}
                                maxLength={15}
                            />
                            <div className="mt-1 flex justify-between text-xs text-gray-500">
                                <span>Characters: {inactiveReason.length}/15</span>
                                <span className={inactiveReason.length > 15 ? 'text-red-500' : inactiveReason.length >= 5 ? 'text-green-500' : 'text-gray-500'}>
                                    {inactiveReason.length < 5 ? 'Too short' : inactiveReason.length > 15 ? 'Too long' : 'Perfect'}
                                </span>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                variant="secondary"
                                onClick={() => setShowInactiveReasonModal(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleInactiveReasonSubmit}
                                disabled={inactiveReason.trim().length < 5 || inactiveReason.trim().length > 15}
                            >
                                Confirm Inactive
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            {/* Image Preview Modal */}
            <ImagePreview 
                src={selectedImageUrl} 
                alt={selectedImageAlt} 
                visible={showImageModal} 
                onVisibleChange={setShowImageModal} 
            />

            {/* Right-click Context Menu */}
            {contextMenu && (
                <div
                    className="fixed z-[9999] min-w-[168px] bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 text-sm overflow-hidden"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 border-b border-gray-100 mb-1">
                        {contextMenu.teacher.firstName} {contextMenu.teacher.lastName}
                    </div>
                    <button
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => { setViewingTeacher(contextMenu.teacher); setContextMenu(null); }}
                    >
                        <Eye className="h-4 w-4 text-gray-400" /> View Details
                    </button>
                    <button
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => { handleEdit(contextMenu.teacher); setContextMenu(null); }}
                    >
                        <Edit className="h-4 w-4 text-gray-400" /> Edit Teacher
                    </button>
                    <div className="my-1 border-t border-gray-100" />
                    <button
                        className="w-full flex items-center gap-2.5 px-3 py-2 transition-colors"
                        style={{ color: contextMenu.teacher.currentPosition.status === 'active' ? '#f59e0b' : '#10b981' }}
                        onClick={() => { handleStatusToggle(contextMenu.teacher.id, contextMenu.teacher.currentPosition.status); setContextMenu(null); }}
                    >
                        {contextMenu.teacher.currentPosition.status === 'active'
                            ? <><ToggleLeft className="h-4 w-4" /> Set Inactive</>
                            : <><ToggleRight className="h-4 w-4" /> Set Active</>
                        }
                    </button>
                </div>
            )}
        </div>
    );
};

const TeacherDetail: React.FC = () => {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Teacher Details</h2>
                <button
                    onClick={() => window.history.back()}
                    className="btn-secondary"
                >
                    Back
                </button>
            </div>
            <div className="card">
                <p className="text-gray-600">Teacher detail view will be implemented here.</p>
            </div>
        </div>
    );
};

const Teachers: React.FC = () => {
    return (
        <Routes>
            <Route path="/" element={<TeachersList />} />
            <Route path="/new" element={<TeachersList />} />
            <Route path="/:id" element={<TeacherDetail />} />
        </Routes>
    );
};

export default Teachers;
